import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { Button, Form, Input, message, Table } from 'antd';
import Title from 'antd/es/typography/Title';
import React, { useState } from 'react';
import { BlobCountry, BlobState } from '../../../../generated';

interface CountriesProps {
  countries: BlobCountry[];
  onSave: (countries: BlobCountry[]) => void;
}

export const Countries: React.FC<CountriesProps> = ({ countries, onSave }) => {
  const [selectedCountries, setSelectedCountries] = useState(countries || []);
  const [isAddingCountry, setIsAddingCountry] = useState(false);
  const [newCountry, setNewCountry] = useState({ countryCode: '', countryName: '' });
  const [isAddingState, setIsAddingState] = useState<number | null>(null);
  const [newState, setNewState] = useState({ countryCode: '', stateCode: '', stateName: '' });

  const handleAddCountry = () => {
    setIsAddingCountry(true);
    setNewCountry({ countryCode: '', countryName: '' });
  };

  const handleRemoveCountry = (index: number) => {
    const updatedCountries = selectedCountries.filter((_, i) => i !== index);
    setSelectedCountries(updatedCountries);
  };

  const handleAddState = (countryIndex: number) => {
    setIsAddingState(countryIndex);
    setNewState({ countryCode: selectedCountries[countryIndex].countryCode ?? '', stateCode: '', stateName: '' });
  };

  const handleRemoveState = (countryIndex: number, stateIndex: number) => {
    const updatedCountries = selectedCountries.map((country, index) => {
      if (index === countryIndex) {
        return {
          ...country,
          states: country?.states?.filter((_, i) => i !== stateIndex)
        };
      }
      return country;
    });
    setSelectedCountries(updatedCountries);
  };

  const handleCountryChange = (value: string, field: string) => {
    setNewCountry((prev) => ({ ...prev, [field]: value }));
  };

  const handleStateChange = (value: string, field: string) => {
    setNewState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCountry = () => {
    let updatedCountries: BlobCountry[] = [];
    if (setSelectedCountries?.length > 0) {
      updatedCountries = [...selectedCountries];
    }
    if (isAddingCountry) {
      if (!newCountry.countryCode || !newCountry.countryName) {
        message.error('Country Code and Country Name are required.');
        return;
      }

      const isDuplicate = selectedCountries.some((country) => country.countryCode === newCountry.countryCode);
      if (isDuplicate) {
        message.error('Country Code already exists.');
        return;
      }

      updatedCountries = [...selectedCountries, { ...newCountry, states: [] }];
      setSelectedCountries(updatedCountries);
      setIsAddingCountry(false);
      setNewCountry({ countryCode: '', countryName: '' });
      onSave(updatedCountries);
    } else {
      onSave(updatedCountries);
    }
  };

  const handleSaveState = (countryIndex: number) => {
    if (!newState.stateCode || !newState.stateName) {
      message.error('State Code and State Name are required.');
      return;
    }

    const isDuplicate = selectedCountries[countryIndex]?.states?.some((state) => state?.stateCode === newState.stateCode);
    if (isDuplicate) {
      message.error('State Code already exists.');
      return;
    }

    setSelectedCountries((prev) =>
      prev.map((country: BlobCountry, index) => {
        if (index === countryIndex) {
          return {
            ...country,
            states: [
              ...(country.states as BlobState[]),
              {
                stateCode: newState.stateCode!,
                stateName: newState.stateName!
              }
            ]
          };
        }
        return country;
      })
    );
    setNewState({ countryCode: '', stateCode: '', stateName: '' });
    setIsAddingState(null);
  };

  const countryColumns = [
    {
      title: 'Country Code',
      dataIndex: 'countryCode',
      key: 'countryCode',
      render: (text: string) => <span>{text}</span>
    },
    {
      title: 'Country Name',
      dataIndex: 'countryName',
      key: 'countryName',
      render: (text: string) => <span>{text}</span>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, _record: any, index: any) => <MinusCircleOutlined className="dynamic-delete-button" onClick={() => handleRemoveCountry(index)} />
    }
  ];

  const stateColumns = [
    {
      title: 'State Code',
      dataIndex: 'stateCode',
      key: 'stateCode',
      render: (text: string) => <span>{text}</span>
    },
    {
      title: 'State Name',
      dataIndex: 'stateName',
      key: 'stateName',
      render: (text: string) => <span>{text}</span>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: { countryIndex: any }, stateIndex: any) => (
        <MinusCircleOutlined className="dynamic-delete-button" onClick={() => handleRemoveState(record.countryIndex, stateIndex)} />
      )
    }
  ];

  const expandedRowRender = (record: BlobCountry, index: number) => (
    <div style={{ paddingLeft: '70px' }}>
      <Table
        columns={stateColumns}
        dataSource={record.states?.map((state: any) => ({
          ...state,
          countryIndex: index
        }))}
        rowKey="stateCode"
        pagination={false}
        size="small"
        style={{ marginBottom: '10px' }}
      />
      {isAddingState === index && (
        <Form layout="inline" style={{ marginTop: '10px' }}>
          <Form.Item>
            <Input placeholder="State Code" value={newState.stateCode} onChange={(e) => handleStateChange(e.target.value, 'stateCode')} />
          </Form.Item>
          <Form.Item>
            <Input placeholder="State Name" value={newState.stateName} onChange={(e) => handleStateChange(e.target.value, 'stateName')} />
          </Form.Item>
          <Form.Item>
            <Button type="dashed" onClick={() => handleSaveState(index)} icon={<PlusOutlined />}>
              Save State
            </Button>
          </Form.Item>
        </Form>
      )}
      <Button type="dashed" onClick={() => handleAddState(index)} icon={<PlusOutlined />}>
        Add State
      </Button>
    </div>
  );

  const handleCancel = () => {
    setIsAddingCountry(false);
    setNewCountry({ countryCode: '', countryName: '' });
    setNewState({ countryCode: '', stateCode: '', stateName: '' });
    setIsAddingState(null);
  };

  return (
    <>
      <PageHeader>
        <div id="leftTitle" style={{ marginBottom: '30px' }}>
          <Title level={3} style={{ display: 'inline' }}>
            Countries
          </Title>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <Title level={4} type="secondary" style={{ display: 'inline' }}>
            Settings
          </Title>
        </div>
      </PageHeader>
      <div>
        <Table
          dataSource={selectedCountries}
          columns={countryColumns}
          rowKey="countryCode"
          pagination={false}
          rowHoverable={false}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => ['CA', 'US'].includes(record.countryCode ?? '')
          }}
          style={{ marginBottom: '10px', width: '600px', border: '1px solid #f0f0f0', borderRadius: '10px', padding: '15px', maxWidth: '100%' }}
        />
        {isAddingCountry ? (
          <div>
            <Form layout="vertical" style={{ display: 'flex', gap: '10px', width: '600px' }}>
              <Form.Item style={{ flex: 1 }}>
                <Input placeholder="Country Code" value={newCountry.countryCode} onChange={(e) => handleCountryChange(e.target.value, 'countryCode')} />
              </Form.Item>
              <Form.Item style={{ flex: 1 }}>
                <Input placeholder="Country Name" value={newCountry.countryName} onChange={(e) => handleCountryChange(e.target.value, 'countryName')} />
              </Form.Item>
            </Form>
          </div>
        ) : (
          <Button type="dashed" onClick={handleAddCountry} block style={{ width: '600px', marginBottom: '10px', float: 'inline-start' }}>
            <PlusOutlined /> Add Country
          </Button>
        )}
        <div style={{ width: '600px', paddingTop: '10px' }}>
          <Button onClick={handleCancel} style={{ marginLeft: '400px' }}>
            Cancel
          </Button>
          <Button type="primary" style={{ marginLeft: '60px' }} onClick={handleSaveCountry}>
            Save
          </Button>
        </div>
      </div>
    </>
  );
};
